import React, { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useRecoilState, useRecoilValue } from "recoil";
import { notificationFilterById } from "actions/Utilities/NotificationFilter";
import AddButton from "components/AddButton";
import ConfirmBoxDeleteTS from "components/ConfirmBoxDeleteTS";
import ConfirmBoxModalTS from "components/ConfirmBoxModalTS";
import EditButton from "components/EditButton";
import MaterialTableTS from "components/tables/MaterialTableTS";
import { toast } from "components/toastNotification";
import { permissionsSelector } from "../../../Login/selectors/permissionSelector";
import { PERMISSIONS } from "../../../PermissionsConstants";
import TableFilters from "../TableFilters";
import { FeeTypeType, recordIdType } from "../types";
import {
  addStateFeeType,
  editStateFeeType,
  rowEditIdStateFeeType,
  selectedRowsStateFeeType,
  setFilterTextFeeType,
} from "./atoms/atoms";
import { FeeTypeForm } from "./forms/FeeTypeForm";
import useDeleteFeeType from "./hooks/useDeleteFeeType";
import useFeeType from "./hooks/useFeeType";
import useUpdateFeeType from "./hooks/useUpdateFeeType";

const FeeTypeTable = () => {
  //Hooks Call
  const { data, isFetching } = useFeeType();
  const UpdateFeeType = useUpdateFeeType();
  const BulkDeleteFeeTypeType = useDeleteFeeType();

  //Atoms Call
  const [actionAddState, setActionAddState] = useRecoilState(addStateFeeType);
  const [selectedRows, setSelectedRows] = useRecoilState(
    selectedRowsStateFeeType
  );
  const [toEdit, setToEdit] = useRecoilState(rowEditIdStateFeeType);
  const [rowsCount, setRowsCount] = useState(0);
  const [actionEditState, setActionEditState] =
    useRecoilState(editStateFeeType);

  //Local States
  const [empTypeIsExist, setEmpTypeIsExistState] = useState(false);
  const [empTypeDataIsChange, setEmpTypeDataIsChange] = useState(false);
  const [checkStatus, setCheckStatus] = useState(false);
  const [openModalDelete, setOpenModalDelete] = useState(false);
  const [openModalEdit, setOpenModalEdit] = useState(false);
  const [hasFeeType, setHasFeeType] = useState(true);
  const [isBulkSelected, setIsBulkSelected] = useState(false);
  const [filterText, setFilterText] = useRecoilState(setFilterTextFeeType);
  const [disabledButton, setDisabledButton] = useState(false);
  const [feeTypeFilteredData, setFeeTypeFilteredData] = useState<FeeTypeType[]>(
    []
  );

  const addPermission = PERMISSIONS.permission_utilities_policyterm_feetype_add;
  const editPermission =
    PERMISSIONS.permission_utilities_policyterm_feetype_edit;
  const deletePermission =
    PERMISSIONS.permission_utilities_policyterm_feetype_delete;
  const userPermissions = useRecoilValue(permissionsSelector);

  const [empNewData] = useState<FeeTypeType[]>([]);

  //For filtered data update
  const [feeTypeId, setFeeTypeId] = useState("");
  useEffect(() => {
    if (feeTypeId?.includes(".")) {
      let ids = feeTypeId.split(".");
      const arr = ids.map((id) => Number(id));
      const filteredData = data?.filter((x) => arr.includes(x.feeTypeId));
      setFeeTypeFilteredData(filteredData);
    } else {
      const filteredData = data?.filter(
        (x) => x.feeTypeId === Number(feeTypeId)
      );
      setFeeTypeFilteredData(filteredData);
    }
  }, [feeTypeId, data]);

  let location = useLocation();
  const history = useHistory();
  let recordIdFromRecent: recordIdType;
  if (location.state != null) {
    recordIdFromRecent!.locationState = location.state as string;
  }

  useEffect(() => {
    if (recordIdFromRecent) {
      try {
        notificationFilterById(
          recordIdFromRecent.recordId,
          recordIdFromRecent.moduleName,
          recordIdFromRecent.subModuleName
        ).then((result) => {
          setFeeTypeFilteredData(result);
          setFeeTypeId(recordIdFromRecent.recordId);
          history.replace("");
        });
      } catch (error) {}
    } else {
      localStorage.removeItem("NotifFilter");
      localStorage.removeItem("ModuleFilter");
    }
  }, []);

  useEffect(() => {
    setEmpTypeIsExistState(false);
    setEmpTypeDataIsChange(false);
    setActionAddState(false);
    setActionEditState(false);
    setSelectedRows([]);
    setToEdit(null);
  }, []);

  const handleSelectedRowsChange = (rows) => {
    const forDelete = rows
      .filter((x) => x.isActive)
      .map((r) => {
        const { tableData, ...rows } = r;
        return rows;
      });
    setSelectedRows(forDelete);
    setRowsCount(forDelete.length);
    setIsBulkSelected((prev) => !prev);
  };

  const handleEditButton = (rowData) => {
    setToEdit({ ...rowData });
    setEmpTypeDataIsChange(false);
    setCheckStatus(rowData.isActive);
    empNewData.map((x) => (x.isActive = rowData.isActive));
    empNewData.map((x) => (x.feeTypeId = rowData.feeTypeId));
    empNewData.map((x) => (x.feeTypeName = rowData.feeTypeName));
    empNewData.map((x) => (x.createdBy = rowData.createdBy));
    empNewData.map((x) => (x.createdDate = rowData.createdDate));
  };

  //Save changes
  const handleSave = (empNewData) => {
    if (empTypeDataIsChange) {
      if (!empTypeIsExist) {
        if (empNewData.filter((x) => x.feeTypeName) !== "") {
          UpdateFeeType(empNewData);
          setToEdit(null);
          setSelectedRows([]);
          setEmpTypeIsExistState(false);
          setEmpTypeDataIsChange(false);
          setOpenModalEdit(false);
          setActionEditState(false);
          setDisabledButton(false);
        } else {
          toast.fire(
            "Saving Failed",
            "All required fields must be filled!",
            "error"
          );
          setOpenModalEdit(false);
        }
      } else {
        toast.fire("Saving Failed", "Fee Type already exists!", "error");
        setOpenModalEdit(false);
      }
    } else {
      toast.fire("Saving Failed", "No Changes have been made", "warning");
      setToEdit(null);
      setOpenModalEdit(false);
      setEmpTypeDataIsChange(false);
      setActionEditState(false);
      setDisabledButton(false);
    }
  };
  // end of saving changes

  const handleBulkDelete = () => {
    const ids = selectedRows.map((row) => row.feeTypeId);
    BulkDeleteFeeTypeType(ids);
    setSelectedRows([]);
    setOpenModalDelete(false);
    setIsBulkSelected(false);
    setDisabledButton(false);
  };

  const deleteMessage = (
    <div className="text-gray-500 font-normal">{`Are you sure you want to deactivate ${rowsCount} ${
      rowsCount > 1 ? "Fee Types" : "Fee Type"
    }?`}</div>
  );

  useEffect(() => {
    if (!toEdit) {
      setDisabledButton(false);
    }
  }, [toEdit]);

  const matColumns = [
    {
      title: "Fee Type Name",
      field: "feeTypeName",
      customSort: (a, b) =>
        a.feeTypeName && b.feeTypeName
          ? a.feeTypeName.trim().localeCompare(b.feeTypeName.trim())
          : a.feeTypeName
          ? 0
          : -1,
      render: (rowData) => {
        return (
          <span className="text-quinary font-bold uppercase text-sm">
            {rowData.feeTypeName}
          </span>
        );
      },
    },
    {
      title: "Created Date",
      field: "createdDate",
      type: "date" as "date",
    },
    {
      title: "Created By",
      field: "createdBy",
      customSort: (a, b) =>
        a.createdBy && b.createdBy
          ? a.createdBy.trim().localeCompare(b.createdBy.trim())
          : a.createdBy
          ? 0
          : -1,
    },
    {
      title: "Modified Date",
      field: "modifiedDate",
      type: "date" as "date",
    },
    {
      title: "Modified By",
      field: "modifiedBy",
      customSort: (a, b) =>
        a.modifiedBy && b.modifiedBy
          ? a.modifiedBy.trim().localeCompare(b.modifiedBy.trim())
          : a.modifiedBy
          ? 0
          : -1,
    },

    {
      title: "Status",
      field: "isActive",
      align: "left",
      render: (rowData) => {
        return (
          <div className="font-sans text-left mr-4 text-xs">
            {rowData.isActive ? "Active" : "Inactive"}
          </div>
        );
      },
    },
    {
      title: "Action/s",
      align: "center",
      sorting: false,
      render: (rowData) => {
        return !userPermissions.includes(editPermission) ? (
          ""
        ) : (
          <div className=" flex items-center place-content-center ">
            <EditButton
              disabled={disabledButton || selectedRows.length > 0}
              className={
                disabledButton || selectedRows.length > 0
                  ? "opacity-50 rounded-full  items-center bg-quaternary text-white hover:bg-hquaternary"
                  : "opacity-100 rounded-full  items-center bg-quaternary text-white hover:bg-hquaternary"
              }
              onClick={() => {
                setDisabledButton(true);
                setActionEditState(true);
                handleEditButton(rowData);
              }}
            />
          </div>
        );
      },
    },
  ];

  //Filters
  const filteredItems = data
    ? data.filter(
        (item) =>
          item.feeTypeName &&
          item.feeTypeName
            .toLowerCase()
            .includes(filterText?.trim().toLowerCase())
      )
    : [];

  const editOrAdd = actionAddState || actionEditState;

  return (
    <div className="bg-white animate-fade-in-down p-4">
      {!editOrAdd && (
        <>
          <div className="flex flex-row ">
            <div className="w-1/2">
              <TableFilters
                defaultValue={filterText}
                filterText={setFilterText}
                moduleDetails="FeeType"
                textInputName="FeeTypeName"
                columnDetails="Fee Type Name"
                disabledButton={disabledButton || selectedRows.length > 0}
              ></TableFilters>
            </div>
            <div className="w-1/2 ">
              {!userPermissions.includes(addPermission) ? (
                ""
              ) : (
                <AddButton
                  buttonText="New Fee Type"
                  className={
                    selectedRows.length > 0 || actionEditState
                      ? "opacity-50 float-right"
                      : "opacity-50 float-right"
                  }
                  onClick={() => setActionAddState(!actionAddState)}
                  disabled={disabledButton || selectedRows.length > 0}
                />
              )}
            </div>
          </div>
          <div>
            <div>
              {userPermissions.includes(deletePermission) ? (
                <div className="text-secondary italic text-xs3 my-2">
                  <p className="font-bold italic inline-block mr-1">Note:</p>
                  <span>Tick the box to deactivate some data.</span>
                </div>
              ) : (
                <div className="mb-4"></div>
              )}
            </div>
          </div>
          <div className="">
            <MaterialTableTS
              columns={matColumns as any}
              data={
                data
                  ? feeTypeFilteredData?.length > 0
                    ? feeTypeFilteredData
                    : filterText === ""
                    ? data
                    : filteredItems
                  : []
              }
              selection={
                !actionEditState && userPermissions.includes(deletePermission)
              }
              setConfirmModalOpen={setOpenModalDelete}
              onSelectionChange={handleSelectedRowsChange}
              selectedRows={selectedRows}
              isLoading={isFetching}
              showTitle={true}
            />
          </div>

          <ConfirmBoxModalTS
            ModalTitle="Update Fee Type"
            qConfirmation=" Are you sure you want to update this?"
            isOpen={openModalEdit}
            onClose={() => {
              setOpenModalEdit(false);
            }}
            onClickFunction={() => {
              handleSave(empNewData);
            }}
          />
          <ConfirmBoxDeleteTS
            ModalTitle="Deactivate Fee Type"
            qConfirmation={deleteMessage}
            isOpen={openModalDelete}
            onClose={() => setOpenModalDelete(false)}
            onClickFunction={
              isBulkSelected ? handleBulkDelete : handleBulkDelete
            }
          />
        </>
      )}
      {editOrAdd && <FeeTypeForm />}
    </div>
  );
};

export default FeeTypeTable;
